import { useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { PlaceWithState } from '@/types/api';
import { OpenBadge } from './OpenBadge';
import { MapActions } from './MapActions';
import { PlaceRowNav } from './PlaceRowNav';
import { HeartIcon, MenuCardIcon } from './Icons';
import { InterestCheck } from './InterestCheck';
import { MenuSheet } from './MenuSheet';
import { PlaceAvatar } from './PlaceAvatar';
import { useInterests } from '@/hooks/useInterests';
import { formatDistance, formatDriveTime, formatPhone, relativeVerifiedLabel } from '@/lib/formatting';
import { displayImageUrl } from '@shared/placeImages';

interface PlaceCardProps {
  place: PlaceWithState;
  detailed?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: (id: number) => void;
}

const ACTION_LINKS: { key: keyof PlaceWithState; label: string }[] = [
  { key: 'menu_url', label: 'Menu' },
  { key: 'reservation_url', label: 'Reserve' },
  { key: 'order_url', label: 'Order' },
  { key: 'booking_url', label: 'Book' },
  { key: 'appointment_url', label: 'Appointment' },
];

export function PlaceCard({ place, detailed = false, isFavorite, onToggleFavorite }: PlaceCardProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isFood = place.category === 'food';
  const { hasPlace, togglePlace } = useInterests();
  const imageUrl = displayImageUrl(place);
  const body = (
    <>
      {imageUrl && (
        <img
          className={detailed ? 'place-card-image place-card-image--hero' : 'place-card-image'}
          src={imageUrl}
          alt=""
          loading={detailed ? 'eager' : 'lazy'}
        />
      )}
      <div className="place-card-header">
        <div className="place-card-header__identity">
          {/* Initials stay under the photo banner as the identity mark.
              Photos live on the 16:9 banner (list + detail), not in this tile. */}
          <PlaceAvatar
            name={place.name}
            slug={place.slug}
            category={place.category}
            size={44}
          />
          <div style={{ minWidth: 0 }}>
            <h3 className="place-card-name">
              {detailed ? place.name : <Link to={`/places/${place.slug}`}>{place.name}</Link>}
            </h3>
            <p className="place-card-meta">
              {formatDistance(place.distance_from_home_miles)} · {formatDriveTime(place.estimated_drive_minutes)} away
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InterestCheck
            checked={hasPlace(place.slug)}
            label={place.name}
            onToggle={() => togglePlace(place.slug)}
          />
          {onToggleFavorite && (
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'transparent', padding: 6, minHeight: 36 }}
              aria-pressed={isFavorite}
              aria-label={isFavorite ? `Remove ${place.name} from Family Favorites` : `Add ${place.name} to Family Favorites`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite(place.id);
              }}
            >
              <HeartIcon filled={isFavorite} style={{ color: isFavorite ? 'var(--color-alert)' : 'var(--color-ink-muted)' }} />
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <OpenBadge state={place.open_state} />
          {place.is_family_favorite && <span className="badge badge-neutral">Family favorite</span>}
        </div>
        {!detailed && <PlaceRowNav place={place} />}
      </div>

      {detailed && place.description && <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.5 }}>{place.description}</p>}

      {detailed && (place.local_take || place.best_for || place.watch_out_for) && (
        <div>
          {place.local_take && (
            <div className="note-block">
              <div className="note-label">Local Take</div>
              {place.local_take}
            </div>
          )}
          {place.best_for && (
            <div className="note-block">
              <div className="note-label">Best For</div>
              {place.best_for}
            </div>
          )}
          {place.watch_out_for && (
            <div className="note-block">
              <div className="note-label">Watch Out For</div>
              {place.watch_out_for}
            </div>
          )}
        </div>
      )}

      {!detailed && place.local_take && <p style={{ marginTop: 8, fontSize: 14, color: 'var(--color-ink-muted)' }}>{place.local_take}</p>}

      <p style={{ marginTop: 10, fontSize: 14 }}>{place.address}</p>
      {place.phone && <p style={{ marginTop: 2, fontSize: 14, color: 'var(--color-ink-muted)' }}>{formatPhone(place.phone)}</p>}

      {detailed && (
        <>
          <MapActions
            address={place.address}
            latitude={place.latitude}
            longitude={place.longitude}
            phone={place.phone}
            website={place.website}
          />
          <div className="action-row">
            {/* In-app menu sheet comes before the external links: seeing the
                dishes without leaving the app is the common case. */}
            {isFood && (
              <button
                type="button"
                className="action-btn"
                onClick={() => setMenuOpen(true)}
                aria-haspopup="dialog"
              >
                <MenuCardIcon width={16} height={16} style={{ marginRight: 6 }} />
                Menu
              </button>
            )}
            {ACTION_LINKS.filter(({ key }) => place[key]).map(({ key, label }) => (
              <a key={key} className="action-btn" href={place[key] as string} target="_blank" rel="noreferrer">
                {label}
              </a>
            ))}
          </div>
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--color-ink-muted)' }}>
            {relativeVerifiedLabel(place.last_verified_at)}
          </p>
        </>
      )}

      {!detailed && (
        <div className="action-row">
          {place.phone && (
            <a
              className="action-btn"
              href={`tel:${place.phone}`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Call ${place.name}`}
            >
              Call
            </a>
          )}
        </div>
      )}
    </>
  );

  if (detailed) {
    return (
      <>
        <div className="card">{body}</div>
        {isFood && (
          <MenuSheet slug={place.slug} name={place.name} category={place.category} open={menuOpen} onClose={() => setMenuOpen(false)} />
        )}
      </>
    );
  }

  // List cards used to be a wrapping <Link>, which nested the Google Maps /
  // Waze <a>s and the ⋯ button inside another <a>. Invalid HTML, and on
  // iOS the inner controls simply did nothing. The name is the real link;
  // tapping the rest of the card still goes to detail unless the tap was
  // on a control (maps, more, Call, Favorites chip).
  return (
    <div
      className="card place-card--tappable"
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('a, button')) return;
        navigate(`/places/${place.slug}`);
      }}
    >
      {body}
    </div>
  );
}
